import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';

type SelectedUser = {
  id: string;
  email: string;
  username: string;
  activated: boolean;
};

type SearchResult = {
  id: string;
  email: string;
};

type ActionKey = 'sendPasswordReset' | 'exportUserData';

type ActionStatus = {
  [key in ActionKey]?: 'loading' | 'success' | 'error';
};

const UserActions: React.FC<{
  initialUserData?: { id: string; email: string } | null;
  onUserUpdated?: () => void;
}> = ({ initialUserData, onUserUpdated }) => {
  const { user } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);
  const [actionStatus, setActionStatus] = useState<ActionStatus>({});

  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialUserData) {
      loadUserData(initialUserData.id);
    }
  }, [initialUserData]);

  const loadUserData = async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      const res = await fetch('/api/admin/users/get', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ userId })
      });

      if (!res.ok) throw new Error('Failed to load user');
      const data = await res.json();

      setSelectedUser({
        id: data.id,
        email: data.email,
        username: data.username || '',
        activated: data.activated === true
      });
      setActionStatus({});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const searchUsers = async () => {
      if (!searchQuery.trim()) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }
      try {
        if (!user) throw new Error('Not authenticated');
        const token = await user.getIdToken();

        const res = await fetch('/api/admin/users/search', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ query: searchQuery })
        });

        if (!res.ok) throw new Error('Search failed');
        const data = await res.json();
        setSearchResults((data.users || []).slice(0, 5));
        setShowDropdown(true);
      } catch (e) {
        setError('Failed to search users');
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectUser = (result: SearchResult) => {
    setSearchQuery('');
    setShowDropdown(false);
    loadUserData(result.id);
  };

  const callAction = async (
    actionKey: ActionKey,
    endpoint: string,
    body: Record<string, unknown>
  ) => {
    if (!selectedUser || !user) return;
    setActionStatus((prev) => ({ ...prev, [actionKey]: 'loading' }));
    try {
      const token = await user.getIdToken();
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('Request failed');
      
      // Handle export action - download as JSON file
      if (actionKey === 'exportUserData') {
        const data = await res.json();
        const userData = data.userData;
        const clientName = userData.clientName || 'unknown';
        const today = new Date();
        const dateStr = today.toISOString().split('T')[0];
        const filename = `ts-${clientName}-${dateStr}.json`;
        
        // Create blob and trigger download
        const blob = new Blob([JSON.stringify(userData, null, 2)], {
          type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      
      setActionStatus((prev) => ({ ...prev, [actionKey]: 'success' }));
      onUserUpdated?.();
    } catch (e) {
      setActionStatus((prev) => ({ ...prev, [actionKey]: 'error' }));
    }
  };

  const statusBadge = (key: ActionKey) => {
    const s = actionStatus[key];
    if (!s) return null;
    const styles: Record<string, string> = {
      loading: 'text-gray-500',
      success: 'text-green-600',
      error: 'text-red-600'
    };
    const labels: Record<string, string> = {
      loading: 'Running…',
      success: 'Done',
      error: 'Failed'
    };
    return (
      <span className={`text-xs font-medium mt-1 ${styles[s]}`}>
        {labels[s]}
      </span>
    );
  };

  type ActionDef = {
    key: ActionKey;
    label: string;
    description: string;
    variant: 'primary' | 'neutral';
    onAction: () => void;
  };

  const actions: ActionDef[] = selectedUser
    ? [
        {
          key: 'sendPasswordReset',
          label: 'Send Password Reset',
          description: 'Email the user a password reset link.',
          variant: 'primary',
          onAction: () =>
            callAction(
              'sendPasswordReset',
              '/api/admin/users/sendPasswordReset',
              { userId: selectedUser.id }
            )
        },
        {
          key: 'exportUserData',
          label: 'Export User Data',
          description: 'Download a JSON export of this user\'s data.',
          variant: 'neutral',
          onAction: () =>
            callAction('exportUserData', '/api/admin/users/export', {
              userId: selectedUser.id
            })
        }
      ]
    : [];

  const variantStyles: Record<ActionDef['variant'], string> = {
    primary: 'bg-light-main text-white hover:bg-light-main-hover',
    neutral: 'bg-light-bg border border-light-border text-light-text hover:bg-gray-100'
  };

  return (
    <div className="p-6 h-full flex flex-col">
      <h2 className="text-2xl font-bold mb-6">User Actions</h2>

      {/* Search */}
      <div className="mb-6 relative">
        <div className="flex-1 relative" ref={dropdownRef}>
          <input
            ref={searchInputRef}
            type="search"
            placeholder="Search by email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchQuery && setShowDropdown(true)}
            className="w-full border border-light-border p-2 rounded-md"
          />
          {showDropdown && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border border-light-border border-t-0 rounded-b-md shadow-lg z-50">
              {searchResults.map((result) => (
                <button
                  key={result.id}
                  onClick={() => handleSelectUser(result)}
                  className="w-full text-left px-4 py-2 hover:bg-light-main-hover hover:text-light-text-alt transition-colors text-sm border-b border-light-border last:border-b-0"
                >
                  {result.email}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Selected user banner */}
      {selectedUser && (
        <div className="mb-6 flex items-center justify-between p-3 bg-gray-50 border border-light-border rounded-md">
          <div className="text-sm">
            <span className="font-semibold">{selectedUser.email}</span>
            {selectedUser.username && (
              <span className="text-gray-500 ml-2">@{selectedUser.username}</span>
            )}
            <span
              className={`ml-3 px-2 py-0.5 rounded text-xs font-medium ${
                selectedUser.activated
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {selectedUser.activated ? 'Active' : 'Inactive'}
            </span>
          </div>
          <button
            onClick={() => {
              setSelectedUser(null);
              setActionStatus({});
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
            type="button"
          >
            Clear
          </button>
        </div>
      )}

      {/* Action grid */}
      {selectedUser && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 overflow-y-auto">
          {actions.map((action) => (
            <div
              key={action.key}
              className="flex flex-col justify-between border border-light-border rounded-lg p-4 bg-white shadow-sm"
            >
              <div className="mb-4">
                <p className="font-semibold text-sm text-light-text">
                  {action.label}
                </p>
                <p className="text-xs text-gray-500 mt-1">{action.description}</p>
              </div>
              <div className="flex flex-col items-start">
                <button
                  type="button"
                  disabled={actionStatus[action.key] === 'loading'}
                  onClick={() => action.onAction()}
                  className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed ${variantStyles[action.variant]}`}
                >
                  {actionStatus[action.key] === 'loading'
                    ? 'Running…'
                    : action.label}
                </button>
                {statusBadge(action.key)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!selectedUser && !loading && (
        <div className="text-center text-gray-500 py-12">
          <p>Search for and select a user to see available actions</p>
        </div>
      )}

    </div>
  );
};

export default UserActions;
